/**
 * VerifyEmail - Email verification page
 * After successful verification:
 * - Auto-redirects to dashboard (3s)
 * - Shows "Open in DuoLyst App" for Gmail/in-app browsers
 * - Attempts duolyst:// deep link fallback
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Mail, ArrowRight, Smartphone } from "lucide-react";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token: authToken } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(3);
  const verifyToken = searchParams.get("token");

  // Detect if we're in an in-app browser (Gmail, Outlook, etc.)
  const isInAppBrowser = typeof navigator !== 'undefined' && (
    /FBAN|FBAV|Instagram|Twitter|LinkedIn|Snapchat/.test(navigator.userAgent) ||
    /GSA\//.test(navigator.userAgent) || // Google Search App
    /YMail|Yahoo/.test(navigator.userAgent)
  );

  // Detect if native app
  const isNativeApp = typeof window !== 'undefined' && window.isNativeApp;

  useEffect(() => {
    if (!verifyToken) {
      setStatus("no-token");
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.post(`${API}/auth/verify-email`, { token: verifyToken });
        setMessage(response.data.message);
        setStatus("success");
      } catch (error) {
        setMessage(error.response?.data?.detail || "Verification failed");
        setStatus("error");
      }
    };

    verifyEmail();
  }, [verifyToken]);

  // Auto-redirect to dashboard after success
  useEffect(() => {
    if (status !== "success") return;

    // If in native app, navigate immediately
    if (isNativeApp) {
      const timer = setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
      return () => clearTimeout(timer);
    }

    // On web: countdown then redirect
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Try deep link first, then navigate
          if (!isNativeApp) {
            try {
              window.location.href = `duolyst://dashboard`;
            } catch (e) {}
            // Fallback: navigate within web app
            setTimeout(() => navigate("/dashboard", { replace: true }), 500);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, isNativeApp, navigate]);

  const handleResendVerification = async () => {
    if (!user?.email) return;
    try {
      await axios.post(`${API}/auth/send-verification`);
      setMessage("Verification email sent! Check your inbox.");
      setStatus("resent");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Failed to resend");
    }
  };

  const handleOpenInApp = () => {
    const deepLink = verifyToken
      ? `duolyst://verify-email?token=${verifyToken}`
      : `duolyst://dashboard`;
    window.location.href = deepLink;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-secondary/5 p-4">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-lg p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-serif font-semibold text-charcoal mb-2">
              Verifying your email...
            </h1>
            <p className="text-muted-foreground">Please wait a moment</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-charcoal mb-2">
              Email Verified!
            </h1>
            <p className="text-muted-foreground mb-4">{message}</p>
            
            {!isNativeApp && countdown > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting to dashboard in {countdown}...
              </p>
            )}

            <Button
              onClick={() => navigate("/dashboard", { replace: true })}
              className="bg-primary hover:bg-primary/90 w-full mb-3"
              data-testid="verify-go-dashboard"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {/* "Open in App" CTA for in-app browsers and web */}
            {!isNativeApp && (
              <Button
                onClick={handleOpenInApp}
                variant="outline"
                className="w-full"
                data-testid="verify-open-in-app"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Open in DuoLyst App
              </Button>
            )}
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-charcoal mb-2">
              Verification Failed
            </h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            
            {user ? (
              <Button onClick={handleResendVerification} className="bg-primary hover:bg-primary/90 w-full mb-3" data-testid="verify-resend">
                <Mail className="w-4 h-4 mr-2" />
                Resend Verification Email
              </Button>
            ) : (
              <Button onClick={() => navigate("/login")} className="bg-primary hover:bg-primary/90 w-full mb-3" data-testid="verify-login">
                Log in to resend
              </Button>
            )}
            
            <Button onClick={() => navigate("/")} variant="outline" className="w-full" data-testid="verify-home">
              Go Home
            </Button>
          </>
        )}

        {status === "no-token" && (
          <>
            <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-6">
              <Mail className="w-10 h-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-charcoal mb-2">
              No Verification Token
            </h1>
            <p className="text-muted-foreground mb-6">
              Please use the link from your verification email.
            </p>
            
            {user ? (
              <Button onClick={handleResendVerification} className="bg-primary hover:bg-primary/90 w-full mb-3">
                <Mail className="w-4 h-4 mr-2" />
                Send Verification Email
              </Button>
            ) : (
              <Button onClick={() => navigate("/login")} className="bg-primary hover:bg-primary/90 w-full">
                Log In
              </Button>
            )}
          </>
        )}

        {status === "resent" && (
          <>
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
              <Mail className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-charcoal mb-2">
              Email Sent!
            </h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full">
              Back to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
